// App entry — v2
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PageTransition } from "@/components/PageTransition";
import { PostHogPageTracker } from "@/components/PostHogPageTracker";
import { lazy, Suspense } from "react";
import Gateway from "./pages/Gateway.tsx";
const Index = lazy(() => import("./pages/Index.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const ServiceAgreement = lazy(() => import("./pages/ServiceAgreement.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const AdminLogin = lazy(() => import("./pages/AdminLogin.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
// Programmatic SEO — city pages (lazy-loaded with data)
const CityPage = lazy(() => import("./components/CityPage.tsx"));

// Blog
const Blog = lazy(() => import("./pages/Blog.tsx"));
const CustomSprinterCostBlog = lazy(() => import("./pages/blog/CustomSprinterCostBlog.tsx"));
const SprintersOverSUVsBlog = lazy(() => import("./pages/blog/SprintersOverSUVsBlog.tsx"));
const HostEarningsBlog = lazy(() => import("./pages/blog/HostEarningsBlog.tsx"));
const BuildProcessBlog = lazy(() => import("./pages/blog/BuildProcessBlog.tsx"));

// Marketplace
const RentAVan = lazy(() => import("./pages/marketplace/RentAVan.tsx"));
const ListYourVan = lazy(() => import("./pages/marketplace/ListYourVan.tsx"));
const HowItWorks = lazy(() => import("./pages/marketplace/HowItWorks.tsx"));
const HostAVan = lazy(() => import("./pages/marketplace/HostAVan.tsx"));

// Host Acquisition Funnel
const HostHub = lazy(() => import("./pages/host/HostHub.tsx"));
const MakeMoneyWithSprinter = lazy(() => import("./pages/host/MakeMoneyWithSprinter.tsx"));
const RevenueCalculator = lazy(() => import("./pages/host/RevenueCalculator.tsx"));
const HandsFreeHosting = lazy(() => import("./pages/host/HandsFreeHosting.tsx"));
const StartRentalBusiness = lazy(() => import("./pages/host/StartRentalBusiness.tsx"));
const OwnerStories = lazy(() => import("./pages/host/OwnerStories.tsx"));
const HostListYourVan = lazy(() => import("./pages/host/ListYourVan.tsx"));
const ManageAFleet = lazy(() => import("./pages/marketplace/ManageAFleet.tsx"));
const FleetManagerApply = lazy(() => import("./pages/marketplace/FleetManagerApply.tsx"));
const FindFleetManager = lazy(() => import("./pages/marketplace/FindFleetManager.tsx"));
const FleetManagerProfile = lazy(() => import("./pages/marketplace/FleetManagerProfile.tsx"));
const FleetManagerSetup = lazy(() => import("./pages/marketplace/FleetManagerSetup.tsx"));
const FleetManagerDashboard = lazy(() => import("./pages/marketplace/FleetManagerDashboard.tsx"));
const MarketplaceAuth = lazy(() => import("./pages/marketplace/MarketplaceAuth.tsx"));
const VerifyEmail = lazy(() => import("./pages/marketplace/VerifyEmail.tsx"));
const ResetPassword = lazy(() => import("./pages/marketplace/ResetPassword.tsx"));
const MarketplaceDashboard = lazy(() => import("./pages/marketplace/Dashboard.tsx"));
const VehicleDetail = lazy(() => import("./pages/marketplace/VehicleDetail.tsx"));
const BookVehicle = lazy(() => import("./pages/marketplace/BookVehicle.tsx"));
const RentalAgreement = lazy(() => import("./pages/marketplace/RentalAgreement.tsx"));
const TripDetail = lazy(() => import("./pages/marketplace/TripDetail.tsx"));
const TripCheckIn = lazy(() => import("./pages/marketplace/TripCheckIn.tsx"));
const TripCheckOut = lazy(() => import("./pages/marketplace/TripCheckOut.tsx"));
const MarketplaceMessages = lazy(() => import("./pages/marketplace/Messages.tsx"));
const CreateListing = lazy(() => import("./pages/marketplace/CreateListing.tsx"));
const LeaveReview = lazy(() => import("./pages/marketplace/LeaveReview.tsx"));
const StripeSetupGuide = lazy(() => import("./pages/marketplace/StripeSetupGuide.tsx"));

// Marketplace City Pages
const DallasVanRentals = lazy(() => import("./pages/marketplace/cities/DallasVanRentals.tsx"));
const HoustonVanRentals = lazy(() => import("./pages/marketplace/cities/HoustonVanRentals.tsx"));
const AustinVanRentals = lazy(() => import("./pages/marketplace/cities/AustinVanRentals.tsx"));
const SanAntonioVanRentals = lazy(() => import("./pages/marketplace/cities/SanAntonioVanRentals.tsx"));
const HelpCenter = lazy(() => import("./pages/marketplace/HelpCenter.tsx"));
const BuyAVanLanding = lazy(() => import("./pages/buy-a-van/BuyAVanLanding.tsx"));
const CommissioningStudio = lazy(() => import("./pages/buy-a-van/CommissioningStudio.tsx"));
const CommercialJourney = lazy(() => import("./pages/buy-a-van/CommercialJourney.tsx"));
const DepositPage = lazy(() => import("./pages/buy-a-van/DepositPage.tsx"));
const DepositSuccess = lazy(() => import("./pages/buy-a-van/DepositSuccess.tsx"));
const FinancialProfile = lazy(() => import("./pages/buy-a-van/FinancialProfile.tsx"));
const BuyerDashboard = lazy(() => import("./pages/buy-a-van/BuyerDashboard.tsx"));
const BuilderDashboard = lazy(() => import("./pages/builder/BuilderDashboard.tsx"));
const BuilderSetup = lazy(() => import("./pages/builder/BuilderSetup.tsx"));
const SupportChatbot = lazy(() => import("@/components/SupportChatbot"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));

const queryClient = new QueryClient();

const SEO = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Loading</p>
      </div>
    </div>
  }>
    <PageTransition>{children}</PageTransition>
  </Suspense>
);

const LazyCityRoute = lazy(() =>
  import("./data/cities").then((mod) => ({
    default: () => {
      const { slug } = useParams<{ slug: string }>();
      const city = mod.getCityBySlug("/rentals/" + slug);
      if (!city) return <Navigate to="/404" replace />;
      return <CityPage city={city} />;
    },
  }))
);

const CityRoute = () => (
  <SEO>
    <LazyCityRoute />
  </SEO>
);

// Lazy-loaded full city directory page
const CityDirectoryPage = lazy(() => import("./pages/CityDirectory.tsx"));

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Gateway /></PageTransition>} />
        <Route path="/sprinter-vans" element={<SEO><Index /></SEO>} />
        <Route path="/pricing" element={<SEO><Pricing /></SEO>} />
        <Route path="/about" element={<SEO><About /></SEO>} />
        <Route path="/contact" element={<SEO><Contact /></SEO>} />
        <Route path="/service-agreement" element={<SEO><ServiceAgreement /></SEO>} />
        <Route path="/privacy" element={<SEO><Privacy /></SEO>} />
        <Route path="/terms" element={<SEO><Terms /></SEO>} />
        <Route path="/admin-login" element={<SEO><AdminLogin /></SEO>} />
        <Route path="/admin" element={<SEO><Admin /></SEO>} />

        {/* Blog */}
        <Route path="/blog" element={<SEO><Blog /></SEO>} />
        <Route path="/blog/how-much-custom-sprinter-van-cost" element={<SEO><CustomSprinterCostBlog /></SEO>} />
        <Route path="/blog/why-executives-choose-sprinter-vans-over-suvs" element={<SEO><SprintersOverSUVsBlog /></SEO>} />
        <Route path="/blog/earn-money-hosting-luxury-van" element={<SEO><HostEarningsBlog /></SEO>} />
        <Route path="/blog/custom-van-build-commission-to-delivery" element={<SEO><BuildProcessBlog /></SEO>} />
        {/* Marketplace */}
        <Route path="/rent-a-van" element={<SEO><RentAVan /></SEO>} />
        <Route path="/marketplace/rent" element={<SEO><RentAVan /></SEO>} />
        <Route path="/list-your-van" element={<SEO><ListYourVan /></SEO>} />
        <Route path="/host-a-van" element={<SEO><HostAVan /></SEO>} />
        <Route path="/host" element={<SEO><HostHub /></SEO>} />
        <Route path="/host/make-money-with-your-sprinter" element={<SEO><MakeMoneyWithSprinter /></SEO>} />
        <Route path="/host/revenue-calculator" element={<SEO><RevenueCalculator /></SEO>} />
        <Route path="/host/hands-free-hosting" element={<SEO><HandsFreeHosting /></SEO>} />
        <Route path="/host/start-a-sprinter-rental-business" element={<SEO><StartRentalBusiness /></SEO>} />
        <Route path="/host/owner-stories" element={<SEO><OwnerStories /></SEO>} />
        <Route path="/host/list-your-van" element={<SEO><HostListYourVan /></SEO>} />
        <Route path="/manage-a-fleet" element={<SEO><ManageAFleet /></SEO>} />
        <Route path="/fleet-manager-apply" element={<SEO><FleetManagerApply /></SEO>} />
        <Route path="/find-fleet-manager" element={<SEO><FindFleetManager /></SEO>} />
        <Route path="/fleet-manager/setup" element={<SEO><FleetManagerSetup /></SEO>} />
        <Route path="/fleet-manager/dashboard" element={<SEO><FleetManagerDashboard /></SEO>} />
        <Route path="/fleet-manager/:id" element={<SEO><FleetManagerProfile /></SEO>} />
        <Route path="/how-it-works" element={<SEO><HowItWorks /></SEO>} />
        <Route path="/marketplace/auth" element={<SEO><MarketplaceAuth /></SEO>} />
        <Route path="/marketplace/verify-email" element={<SEO><VerifyEmail /></SEO>} />
        <Route path="/marketplace/reset-password" element={<SEO><ResetPassword /></SEO>} />
        <Route path="/marketplace/dashboard" element={<SEO><MarketplaceDashboard /></SEO>} />
        <Route path="/marketplace/vehicle/:id" element={<SEO><VehicleDetail /></SEO>} />
        <Route path="/marketplace/book/:id" element={<SEO><BookVehicle /></SEO>} />
        <Route path="/marketplace/rental-agreement" element={<SEO><RentalAgreement /></SEO>} />
        <Route path="/marketplace/trip/:id" element={<SEO><TripDetail /></SEO>} />
        <Route path="/marketplace/trip/:id/check-in" element={<SEO><TripCheckIn /></SEO>} />
        <Route path="/marketplace/trip/:id/check-out" element={<SEO><TripCheckOut /></SEO>} />
        <Route path="/marketplace/messages" element={<SEO><MarketplaceMessages /></SEO>} />
        <Route path="/marketplace/create-listing" element={<SEO><CreateListing /></SEO>} />
        <Route path="/marketplace/review/:tripId" element={<SEO><LeaveReview /></SEO>} />
        <Route path="/marketplace/stripe-setup-guide" element={<SEO><StripeSetupGuide /></SEO>} />

        {/* Marketplace City Pages */}
        <Route path="/rent-a-van/dallas" element={<SEO><DallasVanRentals /></SEO>} />
        <Route path="/rent-a-van/houston" element={<SEO><HoustonVanRentals /></SEO>} />
        <Route path="/rent-a-van/austin" element={<SEO><AustinVanRentals /></SEO>} />
        <Route path="/rent-a-van/san-antonio" element={<SEO><SanAntonioVanRentals /></SEO>} />
        <Route path="/marketplace/help" element={<SEO><HelpCenter /></SEO>} />
        <Route path="/buy-a-van" element={<SEO><BuyAVanLanding /></SEO>} />
        <Route path="/buy-a-van/personal" element={<SEO><CommissioningStudio /></SEO>} />
        <Route path="/buy-a-van/commercial" element={<SEO><CommercialJourney /></SEO>} />
        <Route path="/buy-a-van/deposit" element={<SEO><DepositPage /></SEO>} />
        <Route path="/buy-a-van/deposit-success" element={<SEO><DepositSuccess /></SEO>} />
        <Route path="/buy-a-van/financial-profile" element={<SEO><FinancialProfile /></SEO>} />
        <Route path="/buy-a-van/dashboard" element={<SEO><BuyerDashboard /></SEO>} />
        <Route path="/builder" element={<SEO><BuilderDashboard /></SEO>} />
        <Route path="/builder/setup" element={<SEO><BuilderSetup /></SEO>} />

        {/* Programmatic SEO — City Pages */}
        <Route path="/rentals" element={<SEO><CityDirectoryPage /></SEO>} />
        <Route path="/rentals/:slug" element={<CityRoute />} />

        <Route path="*" element={<SEO><NotFound /></SEO>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    // Sentry.ErrorBoundary catches uncaught React rendering errors
    <Sentry.ErrorBoundary fallback={<p className="p-8 text-center text-muted-foreground">Something went wrong. Please refresh the page.</p>}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <HelmetProvider>
            <BrowserRouter>
              <PostHogPageTracker />
              <AnimatedRoutes />
              <Suspense fallback={null}>
                <SupportChatbot />
              </Suspense>
            </BrowserRouter>
          </HelmetProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
